import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import config from "../../config/api";

const BackendURL = `${config.backendUrl}/filedata`;

export const uploadFile = createAsyncThunk(
    "fileData/uploadFile",
    async (formData, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.post(BackendURL, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const getAllFiles = createAsyncThunk(
    "fileData/getAllFiles",
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get("/filedata/getfiles");
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const getFilesByLead = createAsyncThunk(
    "fileData/getFilesByLead",
    async (leadId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get(`${BackendURL}/${leadId}`);
            return res.data;
        } catch (err) {
            const status = err?.response?.status;
            // "Hard solve" for role-based 403 on per-lead endpoint:
            // fall back to fetching all files and filter client-side.
            // This keeps normal behavior for roles that can access `${BackendURL}/${leadId}`.
            if (status === 401 || status === 403) {
                try {
                    const allRes = await axiosInstance.get("/filedata/getfiles");
                    const allData = allRes.data;
                    const list = Array.isArray(allData)
                        ? allData
                        : Array.isArray(allData?.payload)
                            ? allData.payload
                            : Array.isArray(allData?.files)
                                ? allData.files
                                : [];
                    const filtered = list.filter((f) => {
                        const fid = f?.leadId ?? f?.leadid ?? f?.leadID;
                        return String(fid ?? "") === String(leadId ?? "");
                    });
                    return filtered;
                } catch (fallbackErr) {
                    return rejectWithValue(
                        fallbackErr?.response?.data ||
                        err.response?.data ||
                        fallbackErr.message ||
                        err.message
                    );
                }
            }
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const getFilesByProject = createAsyncThunk(
    "fileData/getFilesByProject",
    async (projectId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get(`${BackendURL}/getproject/${projectId}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const deleteFile = createAsyncThunk(
    "fileData/deleteFile",
    async (id, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.delete(`${BackendURL}/files/${id}`);
            return { id, ...res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const fileDataSlice = createSlice({
    name: "fileData",
    initialState: {
        files: [],
        allFiles: [],
        singleFile: null,
        loading: false,
        error: null,
        success: null,
    },
    reducers: {
        clearFileMessages: (state) => {
            state.error = null;
            state.success = null;
        },
    },
    extraReducers: (builder) => {
        builder

        .addCase(uploadFile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(uploadFile.fulfilled, (state, action) => {
                state.loading = false;
                state.success = "Files uploaded successfully";
                state.files = [...state.files, ...(action.payload.files || [])];
            })
            .addCase(uploadFile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(getAllFiles.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllFiles.fulfilled, (state, action) => {
                state.loading = false;
                state.allFiles = action.payload;
            })
            .addCase(getAllFiles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(getFilesByLead.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getFilesByLead.fulfilled, (state, action) => {
                state.loading = false;
                if (Array.isArray(action.payload)) {
                    state.files = action.payload;
                } else {
                    state.singleFile = action.payload;
                }
            })
            .addCase(getFilesByLead.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(getFilesByProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getFilesByProject.fulfilled, (state, action) => {
                state.loading = false;
                if (Array.isArray(action.payload)) {
                    state.files = action.payload;
                } else {
                    state.singleFile = action.payload;
                }
            })
            .addCase(getFilesByProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(deleteFile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteFile.fulfilled, (state, action) => {
                state.loading = false;
                state.success = "File deleted successfully";
                state.files = state.files.filter((file) => file._id !== action.payload.id);
            })
            .addCase(deleteFile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearFileMessages } = fileDataSlice.actions;
export default fileDataSlice.reducer;
